import React, { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { ChangeLogRequest, ChangeTypeEnum } from '@/api/generated-client'
import { OptionSelector, TextArea } from '@/components/Form'
import { Button } from '@/components'
import { t } from '@/i18n'
import styles from './ChangeLogForm.module.scss'

interface ChangeLogFormProps {
  onSubmit: (data: ChangeLogRequest) => Promise<void>
  onCancel: () => void
  submitting: boolean
}

const changeTypeOptions = [
  { value: ChangeTypeEnum.COMMENT, label: t('change_type_comment') },
  { value: ChangeTypeEnum.CREATE, label: t('change_type_create') },
  { value: ChangeTypeEnum.UPDATE, label: t('change_type_update') },
  { value: ChangeTypeEnum.DELETE, label: t('change_type_delete') },
]

const ChangeLogForm: React.FC<ChangeLogFormProps> = ({
  onSubmit,
  onCancel,
  submitting,
}) => {
  const formRef = useRef<HTMLFormElement>(null)
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<ChangeLogRequest>({
    defaultValues: {
      change_type: ChangeTypeEnum.COMMENT,
      change_reason: '',
    },
  })

  const handleFormSubmit = async (data: ChangeLogRequest) => {
    await onSubmit(data)
    reset()
    onCancel()
  }

  return (
    <div className={styles['change-log-form']}>
      <div className={styles['form-header']}>
        <div className={styles['form-title']}>
          <span className={styles['form-icon']}>📝</span>
          <h4>{t('add_change_log')}</h4>
        </div>
        <p className={styles['form-description']}>
          {t('add_change_log_description')}
        </p>
      </div>
      
      <form ref={formRef} onSubmit={handleSubmit(handleFormSubmit)}>
        <div className={styles['form-container']}>
          <div className={styles['form-row']}>
            <div className={styles['change-type-field']}>
              <OptionSelector
                name="change_type"
                control={control}
                label={t('change_type')}
                options={changeTypeOptions}
                className="w-100"
                required
              />
            </div>
          </div>
          <div className={styles['form-row']}>
            <div className={styles['change-reason-field']}>
              <TextArea
                name="change_reason"
                control={control}
                label={t('change_reason')}
                className="w-100"
                required
                rows={4}
                isEditing={true}
                placeholder={t('change_reason_placeholder')}
              />
            </div>
          </div>
        </div>
      </form>
      <div className={styles['form-actions']}>
        <Button
          className={`${styles['cancel-btn']}`}
          onClick={onCancel}
          disabled={submitting}
        >
          {t('cancel')}
        </Button>
        <Button
          className={`${styles['submit-btn']}`}
          disabled={!isValid || submitting}
          onClick={() =>
            formRef.current?.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true }),
            )
          }
        >
          {submitting ? t('saving') : t('add_change_log')}
        </Button>
      </div>
    </div>
  )
}

export default ChangeLogForm
